import * as React from "react";
import {
	Box, Card, CardContent, CardActionArea, Container, Typography, useTheme, useMediaQuery,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import InventoryRoundedIcon from "@mui/icons-material/InventoryRounded";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import {useNavigate} from "react-router-dom";
import {ThemeProvider} from "@mui/material/styles";
import {buttonTheme, theme} from "../theme";

/* ── Placeholder data ─────────────────────────────────────────────── */
/* Real dashboard cards later, when stats APIs exist. Do not show fake numbers.
const stats = [
	{
		id: 1,
		label: "Total Products",
		value: "1,234",
		icon: (props) => <InventoryRoundedIcon {...props} sx={{color: "success.main", fontSize: 28}}/>,
	},
	{
		id: 2,
		label: "Pending Orders",
		value: "56",
		icon: (props) => <ShoppingCartOutlinedIcon {...props} sx={{color: "warning.main", fontSize: 28}}/>,
	},
	{
		id: 3,
		label: "Monthly Revenue",
		value: "₹12,450",
		icon: (props) => <CreditCardIcon {...props} sx={{color: "secondary.main", fontSize: 28}}/>,
	},
];
*/

const menu = [
	{
		id: 1,
		title: "Departments",
		description: "Organize product categories",
		icon: CategoryOutlinedIcon,
		route: "/md",
		color: "#3B82F6", // Blue
		tint: "rgba(59, 130, 246, 0.1)",
	},
	{
		id: 2,
		title: "Manage Products",
		description: "Add, edit, and organize inventory",
		icon: InventoryRoundedIcon,
		route: "/mp",
		color: "#10B981", // Emerald
		tint: "rgba(16, 185, 129, 0.1)",
	},
	{
		id: 3,
		title: "Manage Orders",
		description: "Process and fulfil orders",
		icon: ShoppingCartOutlinedIcon,
		route: "/orders",
		color: "#F59E0B", // Amber
		tint: "rgba(245, 158, 11, 0.1)",
	},
	{
		id: 4,
		title: "Manage Payment Accounts",
		description: "Add payment methods",
		icon: CreditCardIcon,
		route: "/payments",
		color: "#8B5CF6", // Violet
		tint: "rgba(139, 92, 246, 0.1)",
	},
	{
		id: 5,
		title: "Manage Transactions",
		description: "Track your transactions",
		icon: ShoppingCartOutlinedIcon,
		route: "/transactions",
		color: "#EC4899", // Pink
		tint: "rgba(236, 72, 153, 0.1)",
	},
];

const quickActions = [
	{id: 1, title: "Add New Product", subtitle: "Quickly add items to inventory", route: "/mp"},
	{id: 2, title: "Process Orders", subtitle: "Handle pending customer orders", route: "/orders"},
];

/* ── Component ────────────────────────────────────────────────────── */
export default function MainMenu() {
	const navigate = useNavigate();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	return (
		<Box
			sx={{
				minHeight: "100vh",
				bgcolor: "#F6F9FC",
				py: {xs: 4, md: 8},
			}}
		>
			<ThemeProvider theme={theme}>
				<Container maxWidth="xl">
					{/* ── Header ─────────────────── */}
					<Box mb={6}>
						<Box display="flex" alignItems="center" gap={2} mb={1}>
							<Box
								sx={{
									p: 1,
									backgroundColor: theme.palette.secondary.main,
									borderRadius: 2,
									display: "flex",
								}}
							>
								<TrendingUpIcon sx={{color: "#fff", fontSize: 28}}/>
							</Box>
							<Typography variant={isMobile ? "h5" : "h4"} fontWeight={700}>
								Vendor Dashboard
							</Typography>
						</Box>
						<Typography variant="subtitle1" color="text.secondary">
							Manage your business operations efficiently
						</Typography>
					</Box>

					{/* Real dashboard cards later, when stats APIs exist. Do not show fake numbers.
					<Box
						mb={6}
						sx={{
							display: "grid",
							gap: {xs: 1, md: 2, lg: 3, xl: 3},
							gridTemplateColumns: {
								xs: "1fr",
								md: "repeat(3, 1fr)",
							},
						}}
					>
						{stats.map((s) => (
							<Card
								key={s.id} elevation={0}
								sx={{
									border: 1,
									borderColor: "grey.200",
									borderRadius: 3,
									bgcolor: "#FFF",
								}}
							>
								<CardContent>
									<Box display="flex" justifyContent="space-between" alignItems="center">
										<Box>
											<Typography variant="body2" color="text.secondary">
												{s.label}
											</Typography>
											<Typography variant="h6" fontWeight={700}>
												{s.value}
											</Typography>
										</Box>
										{s.icon()}
									</Box>
								</CardContent>
							</Card>
						))}
					</Box>
					*/}

					{/* ── MAIN MENU ──────────────── */}
					<Box
						mb={8}
						sx={{
							display: "grid",
							gap: {xs: 3, sm: 4},
							gridTemplateColumns: {
								xs: "1fr",             // phones
								sm: "repeat(2, 1fr)",  // small tablets
								md: "repeat(3, 1fr)",  // large tablets
								lg: "repeat(5, 1fr)",  // desktop
							},
						}}
					>
						{menu.map((m) => {
							const Icon = m.icon;
							return (
								<Card
									key={m.id}
									sx={{
										border: 1,
										borderColor: "grey.200",
										borderRadius: 3,
										bgcolor: "#FFF",
										transition: "box-shadow .25s, transform .25s",
										"&:hover": {boxShadow: 4, transform: "translateY(-2px)"},
									}}
									elevation={0}
								>
									<CardActionArea
										onClick={() => navigate(m.route)}
										sx={{p: 3, width: '100%', height: '100%'}}
									>
										<CardContent sx={{ textAlign: "center", p: 0 }}>
											<Box
												sx={{
													mx: "auto", mb: 3, width: 52, height: 52, borderRadius: 3,
													bgcolor: m.tint,
													border: "1px solid",
													borderColor: m.tint,
													display: "flex",
													alignItems: "center", justifyContent: "center",
												}}
											>
												<Icon sx={{ fontSize: 26, color: m.color }} />
											</Box>

											<Typography variant="subtitle1" fontWeight={600} gutterBottom>
												{m.title}
											</Typography>
											<Typography variant="body2" color="text.secondary">
												{m.description}
											</Typography>

											<Box
												sx={{
													mt: 4,
													height: 4,
													width: "80%",
													mx: "auto",
													bgcolor: "grey.100",
													borderRadius: 1,
												}}
											/>
										</CardContent>

									</CardActionArea>
								</Card>
							);
						})}
					</Box>

					{/* ── QUICK ACTIONS ───────────── */}
					<Box
						sx={{
							p: 4,
							border: 1,
							borderColor: "grey.200",
							borderRadius: 3,
							bgcolor: "#FFF",
						}}
					>
						<Typography
							variant="h6"
							fontWeight={600}
							mb={3}
							display="flex"
							alignItems="center"
							gap={1}
						>
							<PeopleAltIcon color="secondary"/> Quick Actions
						</Typography>

						<Box
							sx={{
								display: "grid",
								gap: 3,
								gridTemplateColumns: {
									xs: "1fr",
									md: "repeat(2, 1fr)",
								},
							}}
						>
							{quickActions.map((qa) => (
								<Box
									key={qa.id}
									component="button"
									type="button"
									aria-label={qa.title}
									onClick={() => {
										if (qa.route) navigate(qa.route);
									}}
									sx={{
										textAlign: "left",
										p: 2,
										border: 1,
										borderColor: "grey.300",
										borderRadius: 4,
										bgcolor: "transparent",
										cursor: "pointer",
										typography: "body2",
										"&:hover": {
											borderColor: "primary.main",
											bgcolor: "grey.50",
										},
									}}
								>
									<Typography variant="body1" fontWeight={600} mb={0.5}>
										{qa.title}
									</Typography>
									<Typography variant="subtitle2" color="text.secondary">{qa.subtitle}</Typography>
								</Box>
							))}
						</Box>
					</Box>
				</Container>
			</ThemeProvider>
		</Box>
	);
}
